import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FIELD_STATEMENT_OPERATORS } from '../context/CustomField/CustomFieldTypes'
import { CustomFieldTypes } from '../context/CustomField/CustomFieldTypes'
import GroupConditionType, { GroupConditionOperator } from '../types/GroupCondition'
import { ObjectTypeEnum, SubConditionOperator } from '../types/SubCondition'

export interface GroupConditionState {
    groupConditions: Array<GroupConditionType>
    validated: boolean
}

const defaultSubcondition: GroupConditionType['conditions'][number] = {
    fieldEId: '',
    objectType: ObjectTypeEnum.NOT_SET,
    order: 1,
    operator: null,
    statement: {
        operator: 'ANY',
        values: []
    }
}

const defaultSecondSubcondition: GroupConditionType['conditions'][number] = {
    fieldEId: '',
    objectType: ObjectTypeEnum.NOT_SET,
    order: 1,
    operator: 'AND',
    statement: {
        operator: 'ANY',
        values: []
    }
}

const initialState: GroupConditionState = {
    groupConditions: [],
    validated: false,
}

export const groupConditionsSlice = createSlice({
    name: 'criteriaEngine/GroupConditions',
    initialState,
    reducers: {
        addGroup: (state) => {
            state.groupConditions.push({ order: state.groupConditions.length, operator: state.groupConditions.length === 0 ? null : 'AND', conditions: [defaultSubcondition] })
        },
        deleteGroup: (state, action: PayloadAction<{ groupIndex: number }>) => {
            if (action.payload.groupIndex === 0 && state.groupConditions.length > 1) {
                state.groupConditions[1].operator = null
            }
            state.groupConditions.splice(action.payload.groupIndex, 1)
        },
        updateGroupOperator: (state, action: PayloadAction<{ groupIndex: number, newOperator: NonNullable<GroupConditionOperator> }>) => {
            state.groupConditions[action.payload.groupIndex].operator = action.payload.newOperator
        },
        addSubCondition: (state, action: PayloadAction<{ groupIndex: number }>) => {
            const subConditions = state.groupConditions[action.payload.groupIndex].conditions
            subConditions.push(subConditions.length > 0 ? defaultSecondSubcondition : defaultSubcondition)
        },
        deleteSubCondition: (state, action: PayloadAction<{ groupIndex: number, subConditionIndex: number }>) => {
            const { groupIndex, subConditionIndex } = action.payload
            if (subConditionIndex === 0 && state.groupConditions[groupIndex].conditions.length > 1) {
                state.groupConditions[groupIndex].conditions[1].operator = null
            }
            state.groupConditions[groupIndex].conditions.splice(subConditionIndex, 1)
        },
        updateSubConditionFieldType: (state, action: PayloadAction<{ groupIndex: number, subConditionIndex: number, newFieldType: GroupConditionType['conditions'][number]['objectType'] }>) => {
            const { groupIndex, subConditionIndex, newFieldType } = action.payload

            const currentObjectType = state.groupConditions[groupIndex].conditions[subConditionIndex].objectType
            if (currentObjectType !== newFieldType) {
                const currentSubCondition = state.groupConditions[groupIndex].conditions[subConditionIndex]

                // We want to reset the subcondition to its default state BUT keep the operator selected
                state.groupConditions[groupIndex].conditions[subConditionIndex] = {
                    ...defaultSubcondition,
                    operator: currentSubCondition.operator,
                    objectType: newFieldType,
                }
            }
        },
        updateSubConditionFieldEId: (state, action: PayloadAction<{ groupIndex: number, subConditionIndex: number, fieldEId: string, fieldType: CustomFieldTypes }>) => {
            const { groupIndex, subConditionIndex, fieldEId, fieldType } = action.payload

            const subCondition = state.groupConditions[groupIndex].conditions[subConditionIndex]

            state.groupConditions[groupIndex].conditions[subConditionIndex] = {
                ...subCondition,
                fieldEId: fieldEId,
                statement: {
                    values: [],
                    operator: FIELD_STATEMENT_OPERATORS[fieldType][0]['key'],
                },
            }
        },
        updateSubConditionOperator: (state, action: PayloadAction<{ groupIndex: number, subConditionIndex: number, operator: string }>) => {
            const { groupIndex, subConditionIndex, operator } = action.payload
            state.groupConditions[groupIndex].conditions[subConditionIndex].statement.operator = operator
        },
        updateSubConditionValues: (state, action: PayloadAction<{ groupIndex: number, subConditionIndex: number, newValues: Array<string> | Array<number> }>) => {
            const { groupIndex, subConditionIndex, newValues } = action.payload
            state.groupConditions[groupIndex].conditions[subConditionIndex].statement.values = newValues
        },
        updateSubConditionBaseOperator: (state, action: PayloadAction<{ groupIndex: number, subConditionIndex: number, newOperator: NonNullable<SubConditionOperator> }>) => {
            const { groupIndex, subConditionIndex, newOperator } = action.payload
            state.groupConditions[groupIndex].conditions[subConditionIndex].operator = newOperator
        },
        updateValidated: (state, action: PayloadAction<{ validated: boolean }>) => {
            state.validated = action.payload.validated
        },
    },
})

// Action creators are generated for each case reducer function
export const {
    addGroup,
    deleteGroup,
    updateGroupOperator,
    addSubCondition,
    deleteSubCondition,
    updateSubConditionFieldType,
    updateSubConditionFieldEId,
    updateSubConditionOperator,
    updateSubConditionValues,
    updateSubConditionBaseOperator,
    updateValidated,
} = groupConditionsSlice.actions

export default groupConditionsSlice.reducer


interface ObjectTypeError {
    type: 'objectType'
    message: string
}
interface FieldEIdError {
    type: 'fieldEId'
    message: string
}
interface OperatorError {
    type: 'operator'
    message: string
}
export interface ValueError {
    type: 'value'
    errors: Array<{ valueIndex: number, message: string }>
}
export interface FieldErrorsResponse {
    [key: number]: {
        errors: Array<any>
        subConditions: {
            [key: number]: ObjectTypeError | FieldEIdError | OperatorError | ValueError
        }
    }
}
export function validateFields(groupConditions: Array<GroupConditionType>): FieldErrorsResponse {
    // @ts-ignore
    const response = { groupConditionErrors: [], subConditionErrors: [] }

    for (let groupIndex = 0; groupIndex < groupConditions.length; groupIndex++) {
        const groupCondition = groupConditions[groupIndex]

        if (groupCondition.conditions.length === 0) {
            response.groupConditionErrors.push({ groupIndex, error: { type: 'noSubCondition', message: 'Group condition does not have subconditions' } })
            continue
        }

        const subConditionErrors = validateSubConditions(groupCondition.conditions)

        response.subConditionErrors = subConditionErrors
    }

    const indexedResponse: any = {}

    for (const error of response.subConditionErrors) {
        const { groupIndex, subConditionIndex } = error

        if (!indexedResponse[groupIndex]) {
            indexedResponse[groupIndex] = {
                errors: [],
                subConditions: {},
            }
        }

        indexedResponse[groupIndex].subConditions[subConditionIndex] = error
    }

    return indexedResponse
}

export function validateGroupCondition(groupCondition: GroupConditionType) {
    if (groupCondition.conditions.length === 0) {
        return { type: 'noSubCondition', message: 'Group condition does not have subconditions' }
    }

    const subConditionErrors = validateSubConditions(groupCondition.conditions)

    return subConditionErrors
}

export function validateSubConditions(subConditions: GroupConditionType['conditions']) {
    const response = []

    for (let subConditionIndex = 0; subConditionIndex < subConditions.length; subConditionIndex++) {
        const subCondition = subConditions[subConditionIndex]

        const subConditionError = validateSubCondition(subCondition)

        if (subConditionError) {
            response.push(subConditionError)
        }
    }

    return response
}

export function validateSubCondition(subCondition: GroupConditionType['conditions'][number]): FieldErrorsResponse[number]['subConditions'][number] {
    if (subCondition.objectType === ObjectTypeEnum.NOT_SET) {
        return {
            type: 'objectType',
            message: 'Object type not set',
        }
    }
    if (!subCondition.fieldEId || subCondition.fieldEId === 'NOT_SET' || subCondition.fieldEId === '0') {
        return {
            type: 'fieldEId',
            message: 'Field value not set',
        }
    }
    if (!subCondition.statement.operator) {
        return {
            type: 'operator',
            message: 'Operator not set',
        }
    }

    if (subCondition.statement.values.length === 0) {
        return {
            type: 'value',
            errors: [{
                valueIndex: -1,
                message: 'Need to have at least one value'
            }]
        }
    }

    const valueErrors: ValueError = { type: 'value', errors: [] }
    for (let valueIndex = 0; valueIndex < subCondition.statement.values.length; valueIndex++) {
        const value = subCondition.statement.values[valueIndex]

        if (!value) {
            valueErrors.errors.push({ valueIndex, message: 'Value cannot be blank' })
        }

        // TODO: pass in custom fields and check formats for currency, date, number, etc
    }


    if (valueErrors.errors.length > 0) {
        return valueErrors
    }
}